import LoadingButton from '@/common/components/base/button/loadingButton';
import { NO_ZERO_RULE } from '@/common/components/base/input/textInput/textInput.rules';
import NumberInputWithWords from '@/common/components/base/numberInputWithWords/NumberInputWithWords';
import TitleDescription from '@/common/components/base/typography/TitleDescription';
import Typography from '@/common/components/base/typography/Typography';
import MainLayout from '@/common/components/layouts/MainLayout';
import { requiredText } from '@/common/utils/rules/publicRules';
import {
  GetCostsQueryResponseType,
  TCostsQueryFormType,
} from '@/modules/adjusterReportModules/IncidentParticipants/queries/getCosts/getCostsQuery.type';
import { getCostsQueryOptions } from '@/modules/adjusterReportModules/IncidentParticipants/queries/getCosts/getCostsQueryOptions';
import { usePatchCostQueryOptions } from '@/modules/adjusterReportModules/IncidentParticipants/queries/patchCost/patchCostQueryOptions';
import formatNumberWithCommas from '@/modules/adjusterReportModules/IncidentParticipants/utils/formatNumberWithCommas';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Flex } from 'styled-system/jsx';

export default () => {
  return (
    <MainLayout title="زیان‌دیده / کارشناسی" header="titleHeader">
      <CostsPage />
    </MainLayout>
  );
};

const CostsPage = () => {
  const { id, damagedId } = useParams();
  const navigate = useNavigate();
  const methods = useForm<TCostsQueryFormType>({
    mode: 'onChange',
  });

  const formValues = methods.watch();

  const totalPrice = useMemo(() => {
    // Parse numbers after removing commas
    const parseNumber = (value: any) =>
      Number((value ?? '0').toString().replace(/,/g, ''));

    const addFields = [
      'parts_transportation',
      'parts_payment',
      'parts_cost',
    ] as const;

    const subtractFields = ['parts_daghi', 'parts_reduction'] as const;

    const additions = addFields.reduce((sum, field) => {
      return sum + parseNumber(formValues[field]);
    }, 0);

    const subtractions = subtractFields.reduce((sum, field) => {
      return sum + parseNumber(formValues[field]);
    }, 0);

    return additions - subtractions;
  }, [formValues]);

  const { data } = useQuery(
    getCostsQueryOptions({ id: +id!, damaged_id: +damagedId! }),
  );

  const { mutate: updateCosts, isPending: patchPending } =
    usePatchCostQueryOptions();

  const onSubmit = (formData: TCostsQueryFormType) => {
    updateCosts(
      {
        data: {
          ...formData,
          vehicle_price: Number(
            (formData.vehicle_price ?? '0').toString().replace(/,/g, ''),
          ),
          parts_daghi: Number(
            (formData.parts_daghi ?? '0').toString().replace(/,/g, ''),
          ),
          parts_cost: Number(
            (formData.parts_cost ?? '0').toString().replace(/,/g, ''),
          ),
          parts_payment: Number(
            (formData.parts_payment ?? '0').toString().replace(/,/g, ''),
          ),
          parts_reduction: Number(
            (formData.parts_reduction ?? '0').toString().replace(/,/g, ''),
          ),
          parts_transportation: Number(
            (formData.parts_transportation ?? '0').toString().replace(/,/g, ''),
          ),
          parts_max_body_price: Number(
            (formData.parts_max_body_price ?? '0').toString().replace(/,/g, ''),
          ),
          parts_prev_reduction: Number(
            (formData.parts_prev_reduction ?? '0').toString().replace(/,/g, ''),
          ),
        } as GetCostsQueryResponseType,
        damaged_id: +damagedId!,
        id: +id!,
      },
      {
        onSuccess: () => {
          navigate(-1);
        },
      },
    );
  };

  useEffect(() => {
    if (data) {
      methods.reset({
        ...data,
        parts_daghi: formatNumberWithCommas(data.parts_daghi ?? '0'),
        parts_cost: formatNumberWithCommas(data.parts_cost ?? '0'),
        parts_payment: formatNumberWithCommas(data.parts_payment ?? '0'),
        parts_reduction: formatNumberWithCommas(data.parts_reduction ?? '0'),
        parts_transportation: formatNumberWithCommas(
          data.parts_transportation ?? '0',
        ),
        parts_max_body_price: formatNumberWithCommas(
          data.parts_max_body_price ?? '0',
        ),
        parts_prev_reduction: formatNumberWithCommas(
          data.parts_prev_reduction ?? '0',
        ),
        vehicle_price: formatNumberWithCommas(data.vehicle_price ?? ''),
      });
    }
  }, [data, methods]);

  return (
    <Box pt={24}>
      <TitleDescription>هزینه‌ها</TitleDescription>
      <FormProvider {...methods}>
        <Flex direction="column" gap={8} mt={24}>
          <Box>
            <NumberInputWithWords
              label="هزینه حمل و نقل"
              name="parts_transportation"
              defaultValue="0"
              rules={{ required: requiredText }}
            />
          </Box>
          <Box>
            <NumberInputWithWords
              label="ارزش قطعات داغی"
              name="parts_daghi"
              defaultValue="0"
              rules={{ required: requiredText }}
              disabled
            />
          </Box>
          <Box>
            <NumberInputWithWords
              label="کسورات"
              name="parts_reduction"
              defaultValue="0"
              rules={{ required: requiredText }}
            />
          </Box>
          <Box>
            <NumberInputWithWords
              label="   ارزش روز وسیله‌نقلیه (ریال)"
              name="vehicle_price"
              rules={{ required: requiredText, ...NO_ZERO_RULE }}
              hasZeroAsDefault={false}
            />
          </Box>
          <Box>
            <NumberInputWithWords
              label="جمع لوازم تعویضی (ریال)"
              name="parts_cost"
              defaultValue="0"
              disabled
            />
          </Box>
          <Box>
            <NumberInputWithWords
              label=" اجرت تعمیرات (ریال)"
              name="parts_payment"
              defaultValue="0"
              disabled
            />
          </Box>
          <Box>
            <NumberInputWithWords
              label="بالاترین ارزش لاشه (اختیاری)"
              name="parts_max_body_price"
              defaultValue="0"
            />
          </Box>
          <Box>
            <NumberInputWithWords
              label="کسورات بازدید سلامت قبلی (اختیاری)"
              name="parts_prev_reduction"
              defaultValue="0"
            />
          </Box>
          <CostTotalPrice totalPrice={totalPrice} />
          <Box pb={24}>
            <LoadingButton
              onClick={methods.handleSubmit(onSubmit)}
              loading={patchPending}
              fullWidth
              disabled={totalPrice < 0 || !methods?.formState?.isValid}
            >
              ثبت
            </LoadingButton>
          </Box>
        </Flex>
      </FormProvider>
    </Box>
  );
};

const CostTotalPrice = (data: { totalPrice: number }) => {
  return (
    <Box
      w="100%"
      bg={data?.totalPrice < 0 ? 'pink-2' : 'green-4'}
      px={8}
      py={5}
      borderRadius={4}
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      mt={16}
      mb={30}
    >
      <Typography
        textStyle="b4"
        color={data?.totalPrice < 0 ? 'red' : 'green-1'}
      >
        مجموع هزینه :
      </Typography>
      <Typography
        textStyle="b4"
        color={data?.totalPrice < 0 ? 'red' : 'green-1'}
      >
        {`${data?.totalPrice?.toLocaleString('fa')} ریال`}
      </Typography>
    </Box>
  );
};
